/**
 * Replication of a [SocialNode] over any [P2pSession] transport.
 *
 * This is a thin app-social adapter over :p2p's [ReplicatedEventSync], the transport- and domain-agnostic
 * delta-chain protocol: on connect each side advertises only its per-actor commitment frontier and receives back just
 * the chain suffix it lacks; live authoring is gossiped as the few new events. The earlier Milestone-4 placeholder that
 * pushed the node's whole `export()` on every change is gone — that full-state push was the naive stand-in this layer
 * replaces.
 *
 * The adapter wires the node's two seams to the protocol: incoming events flow through [SocialNode.apply] (so the node's
 * out-of-order re-drive tolerates any delivery order), and locally authored events flow out through
 * [SocialNode.onLocalEvents].
 */

package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.p2p.core.P2pSession
import com.oasisfeng.invaluable.p2p.core.ReplicatedEventSync

class PeerSync(
    node: SocialNode,
    /** Fired whenever applying a peer's event advanced local state, so the host can persist and re-render. */
    onAdvanced: () -> Unit = {},
    log: (String) -> Unit = {},
    onCaughtUp: (P2pSession) -> Unit = {},
) {
    // The node decides which committed-content projections are private (a withheld post, §7.1); the protocol skips them
    // in catch-up deltas so a private body never reaches a peer (its header still does — peers see a missing observation).
    // Directed disclosure envelopes (§20.3) ride the same transport: outbound the node hands them to the sync's
    // best-effort flood, inbound the node opens any addressed to it (a relay forwards without reading, in the sync).
    private val sync = ReplicatedEventSync(node.store, node::commitValidated, onAdvanced = onAdvanced, log = log,
        isWithheld = node::isContentWithheld, onEnvelope = node::receiveEnvelope, onCaughtUp = onCaughtUp)

    init {
        node.onLocalEvents = sync::publishLocal
        node.onLocalEnvelope = sync::sendEnvelope
        // A disclosure wrap is never stored, so its arrival does NOT flow through [onAdvanced] like an event does — yet
        // learning its key advances local state just as much (previously-dark content is now readable). Route it to the
        // same signal so a wrap arriving *after* its ciphertext (the common recommend case) repaints promptly, instead
        // of waiting for the next unrelated event to nudge a render.
        node.onCapabilityLearned = onAdvanced
    }

    /** Wire a freshly opened session into the delta-chain protocol. */
    fun onSessionOpened(session: P2pSession) = sync.onSessionOpened(session)
}
